/* SceneErrorBoundary — route-level error boundary around the scene
 * <Outlet/>. Two failure classes, two behaviors:
 *
 * 1. Chunk-load failure (stale index.html requesting an old hashed chunk
 *    after a redeploy; Vite preload CSS/dep failure). Auto-reload ONCE —
 *    guarded by a timestamped sessionStorage flag so a truly-missing
 *    asset can't reload-loop — else render the "new version deployed"
 *    panel with a manual reload button.
 * 2. Any other scene render error: branded fallback with reload +
 *    back-to-home, raw detail in a collapsed <details>, console.error.
 *
 * Both are reported to App Insights (lib/telemetry.ts), as "chunk-load" or
 * "scene", before any reload.
 *
 * Reset: `resetKey` (AppShell passes location.key) — cleared in
 * componentDidUpdate so any navigation while errored re-attempts the
 * route WITHOUT remounting healthy scenes (a `key=`-based reset would
 * break ProjectView's deliberate cross-/projects/:id persistence).
 * Caveat: React 18 lazy() caches a rejected import on the module
 * singleton, so the soft reset genuinely re-attempts only *render*
 * errors — a chunk-failed route re-throws the cached rejection until a
 * full reload, which is exactly what the chunk path does.
 * Class component because hook error boundaries still don't exist. */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/telemetry';

/* The four real browser/Vite message shapes a rejected dynamic import
   carries: Chromium / Firefox / Safari chunk fetch failures + Vite's
   __vitePreload CSS variant. Pinned by unit tests — re-verify on any
   Vite major bump. */
const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

export function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && CHUNK_ERROR_RE.test(err.message);
}

export const CHUNK_RELOAD_FLAG = 'daedalus:chunk-reload-at';
const CHUNK_RELOAD_SUPPRESS_MS = 60_000;

/** True ⇒ caller may auto-reload now (and has been stamped); false ⇒ show manual UI. */
export function markAndCheckAutoReload(
  store: Pick<Storage, 'getItem' | 'setItem'>,
  now: number
): boolean {
  const prev = Number(store.getItem(CHUNK_RELOAD_FLAG) ?? NaN);
  if (Number.isFinite(prev) && now - prev < CHUNK_RELOAD_SUPPRESS_MS) return false;
  store.setItem(CHUNK_RELOAD_FLAG, String(now)); // may throw — caller catches
  return true;
}

interface Props {
  resetKey: string;                 // location.key from AppShell
  onNavigateHome: () => void;       // navigate('/home') from AppShell
  children: ReactNode;
}
interface State { error: Error | null; }

export class SceneErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[SceneErrorBoundary] scene failed:', error, info);
    reportError(error, isChunkLoadError(error) ? 'chunk-load' : 'scene');
    if (isChunkLoadError(error)) {
      let reloadNow = false;
      try {
        reloadNow = markAndCheckAutoReload(window.sessionStorage, Date.now());
      } catch { /* storage unavailable — manual panel, never loop */ }
      if (reloadNow) window.location.reload();
      // else: state.error is set; render() shows the chunk panel.
    }
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });   // navigation while errored → re-attempt
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return isChunkLoadError(error) ? (
      <SceneFallback
        headline="A new version of Daedalus was deployed."
        body="This tab is holding an old build. Reload to pick up the new one — your work in other tabs is unaffected."
        error={error}
        actions={
          <button type="button" className="btn accent" onClick={() => window.location.reload()}>
            reload
          </button>
        }
      />
    ) : (
      <SceneFallback
        headline="This screen hit an error."
        body="The rest of the app is still running — you can go back home, or reload this screen."
        error={error}
        actions={
          <>
            <button type="button" className="btn accent" onClick={() => window.location.reload()}>
              reload
            </button>
            <button type="button" className="btn" onClick={this.props.onNavigateHome}>
              back to home
            </button>
          </>
        }
      />
    );
  }
}

export function SceneFallback(props: {
  headline: string; body: string; error: Error; actions: ReactNode;
}) {
  return (
    <div className="col fill" style={{ alignItems: 'center', justifyContent: 'center', padding: 32, background: 'var(--bg-1)' }} role="alert">
      <div style={{ maxWidth: 520, width: '100%', padding: 24, border: '1px solid var(--rule-soft)', background: 'var(--bg-2)' }}>
        <div className="d-lg" style={{ marginBottom: 8 }}>{props.headline}</div>
        <div className="mono-lc" style={{ color: 'var(--ink-2)', marginBottom: 16 }}>{props.body}</div>
        <div className="row gap-2">{props.actions}</div>
        <details style={{ marginTop: 16 }}>
          <summary className="mono-lc" style={{ cursor: 'pointer', color: 'var(--ink-3)' }}>technical detail</summary>
          <pre className="mono-lc" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--ink-3)', fontSize: 12, marginTop: 8 }}>
            {props.error.message}
          </pre>
        </details>
      </div>
    </div>
  );
}
